import { SearchOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Flex,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { IUkhoUnmatchedReason, IUkhoUnmatchedRow } from "interfaces";
import React, { useMemo, useState } from "react";
import { apiErrorMessage, useUnmatched } from "./api";
import {
  formatDay,
  matchesReasonFilter,
  noticeKindWord,
  shortServices,
  UNMATCHED_REASON_FILTER_OPTIONS,
  UnmatchedReasonFilter,
} from "./lib";
import { MatchToShipModal } from "./MatchToShipModal";
import { OriginalEmailModal } from "./OriginalEmailModal";
import { useDebouncedValue } from "./useDebouncedValue";
import { WrappingTag } from "./workTableColumns";

/** Spec 11.3 chip colours: grey, blue for arriving notices, amber for the rest. */
const REASON_CHIP_COLORS: Record<IUkhoUnmatchedReason, string | undefined> = {
  "ship not in eMariner": undefined,
  "ship not in eMariner yet": "blue",
  "no IMO, licence unknown": "gold",
  "licence found, no ship attached": "gold",
  "sent back by hand": "gold",
};

interface UnmatchedTabProps {
  /** Refreshes the live counts on the tab labels. */
  refetchCounts: () => void;
}

/**
 * Spec 11.3 "Unmatched notices": notices eMariner could not tie to a ship,
 * newest first (the API's order). The reason filter runs on the client over
 * the fetched list; the search goes to the API.
 */
export const UnmatchedTab: React.FC<UnmatchedTabProps> = ({ refetchCounts }) => {
  const [searchText, setSearchText] = useState("");
  const [reasonFilter, setReasonFilter] = useState<UnmatchedReasonFilter>("all");
  const [noticeToMatch, setNoticeToMatch] = useState<IUkhoUnmatchedRow | null>(
    null
  );
  const [emailNoticeId, setEmailNoticeId] = useState<number | null>(null);
  const search = useDebouncedValue(searchText);
  const { data, isLoading, isFetching, isError, error, refetch } =
    useUnmatched(search);

  const shownRows = useMemo(
    () => data?.filter((row) => matchesReasonFilter(row.reason, reasonFilter)),
    [data, reasonFilter]
  );

  const handleMatched = () => {
    setNoticeToMatch(null);
    refetch();
    refetchCounts();
  };

  const columns: ColumnsType<IUkhoUnmatchedRow> = [
    {
      title: "UKHO day",
      key: "noticeDate",
      width: 120,
      render: (_, row) => formatDay(row.noticeDate),
    },
    {
      title: "What",
      key: "kind",
      width: 90,
      render: (_, row) => (
        <Tag color={row.kind === "in" ? "green" : "red"}>
          {noticeKindWord(row.kind)}
        </Tag>
      ),
    },
    { title: "Licence", dataIndex: "ukhoLicenceId", key: "licence", width: 100 },
    {
      title: "Ship name (UKHO)",
      dataIndex: "licenceName",
      key: "licenceName",
      width: 180,
      className: "wrap-text",
    },
    {
      title: "IMO",
      key: "imoNumber",
      width: 120,
      render: (_, row) =>
        row.imoNumber ?? <Typography.Text type="secondary">none</Typography.Text>,
    },
    {
      title: "Shipping company (UKHO)",
      key: "shippingCompany",
      width: 200,
      className: "wrap-text",
      render: (_, row) => row.shippingCompany ?? "",
    },
    {
      title: "Services",
      key: "services",
      width: 140,
      className: "wrap-text",
      render: (_, row) => shortServices(row.services),
    },
    {
      title: "Why unmatched",
      key: "reason",
      width: 190,
      className: "wrap-text",
      render: (_, row) => (
        <WrappingTag color={REASON_CHIP_COLORS[row.reason]}>
          {row.reason}
        </WrappingTag>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 180,
      className: "wrap-text",
      render: (_, row) => (
        <Space size="small" wrap>
          <Button size="small" onClick={() => setNoticeToMatch(row)}>
            Match to ship
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => setEmailNoticeId(row.id)}
          >
            email
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Text type="secondary">
        Notices eMariner could not tie to a ship. Nothing is lost: a notice
        matches by itself once a ship or licence with its number exists, or a
        person matches it here.
      </Typography.Text>
      <Flex align="center" gap="middle" wrap>
        <Select<UnmatchedReasonFilter>
          value={reasonFilter}
          onChange={setReasonFilter}
          options={UNMATCHED_REASON_FILTER_OPTIONS}
          style={{ width: 220 }}
        />
        <Input
          placeholder="Search name, licence or IMO"
          prefix={<SearchOutlined />}
          allowClear
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          style={{ width: 260 }}
        />
      </Flex>
      {isError ? (
        <Alert type="error" showIcon message={apiErrorMessage(error)} />
      ) : (
        <Table<IUkhoUnmatchedRow>
          rowKey="id"
          columns={columns}
          dataSource={shownRows}
          loading={isLoading || isFetching}
          pagination={{ defaultPageSize: 50, hideOnSinglePage: true }}
        />
      )}
      {noticeToMatch && (
        <MatchToShipModal
          open
          notice={noticeToMatch}
          onClose={() => setNoticeToMatch(null)}
          onDone={handleMatched}
        />
      )}
      <OriginalEmailModal
        noticeId={emailNoticeId}
        onClose={() => setEmailNoticeId(null)}
      />
    </Space>
  );
};
